import React from 'react';
import cn from 'classnames';
import omit from 'lodash/omit';
import noop from 'lodash/noop';
import BodyCell from '../BodyCell';
import { IColumn, IRowData } from '../../interfaces';

export interface IBodyRowProps {
  rowIndex: number,
  rowData: { [key: string]: any },
  isLastChild?: boolean,
  columns: IColumn[],
  rowCallbacks?: {
    [key: string]: (event: Event, rowData: IRowData) => void,
  },
  rowClassNameCallback?: (rowData: IRowData) => string,
  isSelected?: boolean,
  bodyCellComponent?: React.ElementType,
  listeners?: object,
  attributes?: object,
  dragAttributes?: object,
  dragListeners?: object,
  isOver?: boolean,
  isDragging?: boolean,
  isDropAvailable?: boolean,
  style?: object,
  className?: string,
  isAnimation?: boolean,
  onMouseOverCapture?: () => void,
  onMouseLeave?: () => void,
  isHovered?: boolean,
  customCellProps?: { [key: string]: any },
  dataTest?: string,
}

const BodyRow = React.forwardRef(({
  rowIndex,
  rowData,
  isLastChild = false,
  columns,
  rowCallbacks = {},
  rowClassNameCallback = () => '',
  isSelected = false,
  bodyCellComponent: BodyCellComponent = BodyCell,
  listeners = {},
  attributes = {},
  dragAttributes = {},
  dragListeners = {},
  isOver = false,
  isDragging = false,
  isDropAvailable = false,
  style,
  className,
  onMouseOverCapture = noop,
  onMouseLeave = noop,
  isHovered,
  customCellProps,
  dataTest = null,
}: IBodyRowProps, ref) => {
  const callbacks = Object.keys(rowCallbacks).reduce((callbacks, callbackName) => ({
    ...callbacks,
    [callbackName]: (event) => {
      rowCallbacks[callbackName](event, rowData);
    },
  }), {});

  return (
    <tr
      className={ cn(rowIndex % 2 === 0 ? 'odd' : 'even', className, rowClassNameCallback(rowData), {
        'is-first-child': rowIndex === 0,
        'is-last-child': isLastChild,
        'is-selected': isSelected || (isOver && !isDragging && isDropAvailable),
        'is-disabled': rowData.isDisabled,
      }) }
      // @ts-ignore
      ref={ ref }
      { ...callbacks }
      data-test={ dataTest ?? `row[${ rowIndex }]` }
      data-row={ rowIndex }
      style={ style }
      onMouseOverCapture={ onMouseOverCapture }
      onMouseLeave={ onMouseLeave }
      { ...dragAttributes }
      { ...dragListeners }
    >
      {
        columns.map((column) => (
          <BodyCellComponent
            key={ column.name }
            rowIndex={ rowIndex }
            rowData={ rowData }
            listeners={ listeners }
            attributes={ attributes }
            isHovered={ isHovered }
            customCellProps={ customCellProps }
            { ...omit(column, 'CustomHeadCell', 'additionalSortOptions', 'tooltipMaxWidth') }
          />
        ))
      }
    </tr>
  );
});

export default BodyRow;
